import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSalon } from '../store/slice/salonSlice';
import { useBooking } from '../store/slice/bookingSlice';
import { ActionButton, StatusToggle } from '../components/ActionControls';
import {
  clientLabel,
  dateLabel,
  serviceLabel,
  statusLabel,
  timeLabel,
} from '../utils/bookingDisplay';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function isActiveSalon(salon) {
  return salon?.is_active === 1 || salon?.is_active === true;
}

function getSalonApprovalStatus(salon) {
  const rawStatus = salon?.approval_status ?? salon?.approved ?? salon?.status;
  if (typeof rawStatus === 'string') {
    return rawStatus.toLowerCase();
  }
  if (salon?.is_approved === 1 || salon?.is_approved === true || salon?.approved === true) {
    return 'approved';
  }
  if (salon?.is_approved === 0 || salon?.is_approved === false || salon?.approved === false) {
    return 'rejected';
  }
  return 'applied';
}

function isPendingApprovalSalon(salon) {
  const status = getSalonApprovalStatus(salon);
  return status === 'pending' || status === 'applied';
}

function formatApprovalLabel(status) {
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Rejected';
  if (status === 'applied') return 'Applied';
  return 'Pending';
}

function getSalonInitials(name) {
  const words = (name || 'Salon')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);

  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || 'SL';
}

function SalonDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    selectedSalon,
    pendingApprovalSalons,
    loading,
    error,
    fetchSalonById,
    fetchPendingApprovalSalons,
    updateSalonApproval,
    deleteSalon,
    toggleSalonActive,
  } = useSalon();
  const { bookings, loading: bookingsLoading, fetchBookingsBySalon } = useBooking();

  useEffect(() => {
    if (id) fetchSalonById(id);
  }, [id, fetchSalonById]);

  useEffect(() => {
    fetchPendingApprovalSalons();
  }, [fetchPendingApprovalSalons]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (id) fetchBookingsBySalon(id);
  }, [id, fetchBookingsBySalon]);

  const handleDelete = async () => {
    if (selectedSalon && window.confirm(`Delete "${selectedSalon.name}"?`)) {
      const result = await deleteSalon(selectedSalon.id);
      if (result?.meta?.requestStatus === 'fulfilled') {
        toast.success('Salon deleted successfully');
        navigate('/salons');
      }
    }
  };

  const handleToggleActive = async () => {
    if (!selectedSalon) return;
    const isActive = isActiveSalon(selectedSalon);
    const result = await toggleSalonActive(selectedSalon.id);
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success(`Salon marked as ${isActive ? 'inactive' : 'active'}`);
    }
  };

  const handleUpdateApproval = async (approved) => {
    if (!selectedSalon) return;

    if (approved === 'rejected' && !window.confirm(`Are you sure you want to reject "${selectedSalon.name}"?`)) {
      return;
    }

    const result = await updateSalonApproval({ salonId: selectedSalon.id, approved });
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success(
        approved === 'approved'
          ? `Approved "${selectedSalon.name}" successfully`
          : `Rejected "${selectedSalon.name}" successfully`
      );
      fetchPendingApprovalSalons();
      fetchSalonById(selectedSalon.id);
    }
  };

  if (loading && !selectedSalon) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading salon...</p>
      </div>
    );
  }

  if (!selectedSalon) {
    return (
      <div className="space-y-4">
        <Link to="/salons" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Salons
        </Link>
        <div className="card-premium py-16 text-center text-slate-500">Salon not found.</div>
      </div>
    );
  }

  const salon = selectedSalon;
  const hours = salon.hours || [];
  const approvalStatus = getSalonApprovalStatus(salon);
  const isPendingApproval =
    pendingApprovalSalons.some((pendingSalon) => String(pendingSalon.id) === String(salon.id)) ||
    isPendingApprovalSalon(salon);

  const salonName = salon.name || `Salon #${salon.id}`;
  const isActive = isActiveSalon(salon);
  const addressLine = [salon.address_line_1, salon.address_line_2].filter(Boolean).join(', ') || 'Address not added';
  const locationLine =
    [salon.city, salon.state, salon.postal_code, salon.country].filter(Boolean).join(', ') || 'Location not added';
  const primaryContact = salon.phone || salon.email || 'Not available';
  const statCards = [
    { label: 'Status', value: isActive ? 'Active' : 'Inactive' },
    { label: 'Approval', value: formatApprovalLabel(approvalStatus) },
    { label: 'Gallery', value: `${salon.images?.length || 0} images` },
    { label: 'Bookings', value: `${bookings.length} total` },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Link
          to="/salons"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Salons
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          {isPendingApproval && (
            <>
              <ActionButton
                type="button"
                variant="success"
                onClick={() => handleUpdateApproval('approved')}
                disabled={loading}
              >
                Approve
              </ActionButton>
              <ActionButton
                type="button"
                variant="danger"
                onClick={() => handleUpdateApproval('rejected')}
                disabled={loading}
              >
                Reject
              </ActionButton>
            </>
          )}
          <StatusToggle
            checked={isActive}
            onChange={handleToggleActive}
            disabled={loading}
            label={`Toggle salon status for ${salonName}`}
          />
          <ActionButton
            type="button"
            variant="danger"
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </ActionButton>
        </div>
      </div>

      <div className="card-premium overflow-hidden">
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-amber-700 px-6 pb-16 pt-6 sm:px-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.18),_transparent_35%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(135deg,_rgba(255,255,255,0.08),_transparent_45%)]" />

          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="inline-flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl border border-white/15 bg-white/10 text-xl font-bold tracking-[0.24em] text-white backdrop-blur">
                {getSalonInitials(salonName)}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/85 backdrop-blur">
                    {locationLine}
                  </span>
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${
                      isPendingApproval
                        ? 'bg-amber-200/90 text-amber-900'
                        : 'bg-emerald-300/90 text-emerald-950'
                    }`}
                  >
                    {formatApprovalLabel(approvalStatus)}
                  </span>
                </div>

                <h1 className="mt-4 break-words font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  {salonName}
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-white/75">
                  {salon.description || 'No description has been added for this salon yet.'}
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60">
                Visibility
              </p>
              <div className="mt-2 flex items-center gap-3">
                <StatusToggle
                  checked={isActive}
                  onChange={handleToggleActive}
                  disabled={loading}
                  label={`Toggle salon status for ${salonName}`}
                  showStateLabel={false}
                />
                <span className="text-sm font-semibold text-white">
                  {isActive ? 'Live on platform' : 'Hidden from platform'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative -mt-10 px-6 pb-6 sm:px-8">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {statCards.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-slate-200/70 bg-white/95 p-4 shadow-premium backdrop-blur"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  {stat.label}
                </p>
                <p className="mt-2 text-base font-semibold text-slate-800">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="card-premium p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Business information</h2>
                <p className="mt-1 text-sm text-slate-500">Primary address and contact details for this listing.</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                Salon ID #{salon.id}
              </span>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-xl bg-amber-50 p-2 text-amber-600">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M12 21c4-4.35 6-7.46 6-10a6 6 0 10-12 0c0 2.54 2 5.65 6 10z"
                      />
                      <circle cx="12" cy="11" r="2.2" strokeWidth={1.8} />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      Address
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-700">{addressLine}</p>
                    <p className="text-sm leading-6 text-slate-500">{locationLine}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-xl bg-emerald-50 p-2 text-emerald-600">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.8}
                        d="M3 5h18v14H3V5zm2 2l7 5 7-5"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      Contact
                    </p>
                    <div className="mt-2 space-y-2 text-sm text-slate-700">
                      <p className="break-words">{salon.phone || 'Phone not added'}</p>
                      <p className="break-words">{salon.email || 'Email not added'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card-premium p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Operating hours</h2>
                <p className="mt-1 text-sm text-slate-500">Availability configured for this salon.</p>
              </div>
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                {hours.length} {hours.length === 1 ? 'entry' : 'entries'}
              </span>
            </div>

            {hours.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center text-sm text-slate-500">
                No business hours have been added yet.
              </div>
            ) : (
              <div className="space-y-2">
                {hours.map((h) => (
                  <div
                    key={h.day_of_week}
                    className="flex items-center justify-between rounded-2xl border border-slate-100 px-4 py-3"
                  >
                    <div>
                      <p className="font-medium text-slate-800">
                        {DAY_NAMES[h.day_of_week] ?? `Day ${h.day_of_week}`}
                      </p>
                      <p className="text-xs text-slate-400">
                        {h.is_closed ? 'Closed for the day' : 'Open for bookings'}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-sm font-medium ${
                        h.is_closed
                          ? 'bg-slate-100 text-slate-500'
                          : 'bg-emerald-50 text-emerald-700'
                      }`}
                    >
                      {h.is_closed ? 'Closed' : `${h.open_time || '—'} – ${h.close_time || '—'}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-premium p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Recent bookings</h2>
                <p className="mt-1 text-sm text-slate-500">Latest appointments connected to this salon.</p>
              </div>
              <Link to="/bookings" className="text-sm font-medium text-amber-600 hover:text-amber-700">
                View all
              </Link>
            </div>

            {bookingsLoading && bookings.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center text-sm text-slate-500">
                Loading bookings...
              </div>
            ) : bookings.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center text-sm text-slate-500">
                No bookings for this salon yet.
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.slice(0, 5).map((b) => (
                  <div
                    key={b.id}
                    className="flex flex-col gap-3 rounded-2xl border border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{clientLabel(b)}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        {serviceLabel(b)} · {dateLabel(b) || '—'} {timeLabel(b) ? `at ${timeLabel(b)}` : ''}
                      </p>
                    </div>
                    <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold capitalize text-slate-600">
                      {statusLabel(b) || '—'}
                    </span>
                  </div>
                ))}
                {bookings.length > 5 && (
                  <p className="px-1 text-xs text-slate-400">+{bookings.length - 5} more bookings</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="card-premium p-6">
            <h2 className="text-lg font-semibold text-slate-800">Quick summary</h2>
            <div className="mt-5 space-y-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  Primary contact
                </p>
                <p className="mt-2 text-sm font-medium text-slate-700 break-words">{primaryContact}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  Listing status
                </p>
                <p className="mt-2 text-sm font-medium text-slate-700">
                  {isPendingApproval ? 'Awaiting admin approval' : 'Approved and available for management'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  Visibility
                </p>
                <p className="mt-2 text-sm font-medium text-slate-700">
                  {isActive ? 'Currently visible to customers' : 'Currently hidden from customers'}
                </p>
              </div>
            </div>
          </div>

          <div className="card-premium p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Gallery</h2>
                <p className="mt-1 text-sm text-slate-500">Uploaded salon images.</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                {salon.images?.length || 0}
              </span>
            </div>

            {!salon.images?.length ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center text-sm text-slate-500">
                No images uploaded for this salon.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {salon.images.map((img) => (
                  <img
                    key={img.id}
                    src={`${process.env.REACT_BASE_URL}${img.image_url}`}
                    alt={`${salonName} gallery`}
                    className="h-32 w-full rounded-2xl border border-slate-200 object-cover"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalonDetailPage;
